import { getAceSelectNames, aceSelectCssVariables } from "./ace-select.get-names";
import { h, forceUpdate, Fragment } from "@stencil/core";
import { getProperty, getRandomString, mergeClassNames, createCssVariables, JsonParser, objectsAreEqual, DOWN_ARROW_KEY_CODE, ENTER_KEY_CODE, ESCAPE_KEY_CODE, SPACEBAR_KEY_CODE, UP_ARROW_KEY_CODE, TAB_KEY_CODE, } from "../../utils";
import { parseStringToHtmlNodes } from "./utils";
export class AceSelect {
    constructor() {
        this.direction = 'down';
        // Options for the dropdown list.
        this.options = [];
        this.disabled = false;
        // If set to true, then we can select multiple items at once.
        this.multiselect = false;
        this.errors = [];
        // If set to `true`, the select renders in an error state. If errors are defined within the `errors` array this property is set automatically to `true`.
        this.hasErrors = false;
        // If set to `true`, the select renders in an open state. Used mainly for testing purposes.
        this.open = false;
    }
    componentWillLoad() {
        var _a;
        // If an ID is passed on the host element then we should not generate one.
        // TODO: This internal ID should also be refactored to use
        const externalId = this.hostElement.getAttribute('id');
        this._internalId = externalId ? externalId : getRandomString('ace-select', 12);
        this.names = getAceSelectNames((_a = this.hostElement) === null || _a === void 0 ? void 0 : _a.id);
        this.parseOptions();
        this.parseSelected();
        this.parseErrors();
        this.parseAndApplyCustomCss();
    }
    componentDidRender() {
        if (this.selectedFieldRef && this.selectionHeight !== this.selectedFieldRef.offsetHeight) {
            this.selectionHeight = this.selectedFieldRef.offsetHeight;
            forceUpdate(this);
        }
    }
    parseOptions() {
        this._options = new JsonParser(this.options).getValue();
    }
    parseSelected() {
        this._selected = new JsonParser(this.selected, this.multiselect ? [] : {}).getValue();
        if (Array.isArray(this._selected) && !this.multiselect) {
            this.multiselect = true;
        }
        if (!Array.isArray(this._selected) && this.multiselect) {
            this.multiselect = false;
        }
    }
    parseErrors() {
        this._errors = new JsonParser(this.errors, []).addIdsToArray('error').getValue();
    }
    parseAndApplyCustomCss() {
        if (this.customCss) {
            const styleId = `${this._internalId}__custom-css`;
            let style = this.hostElement.shadowRoot.getElementById(styleId);
            if (!style) {
                style = document.createElement('style');
                style.id = styleId;
            }
            style.textContent = this.customCss;
            this.hostElement.shadowRoot.appendChild(style);
        }
    }
    /**
     *
     * @private
     */
    shouldRenderErrors() {
        return this.hasErrors || (this._errors && this._errors.length > 0);
    }
    closeDropdown(event, forceClose = false) {
        const path = event.composedPath();
        // If the click is outside of the component, then close the dropdown.
        if (!path.includes(this.hostElement) || forceClose) {
            this.open = false;
        }
    }
    /**
     * @private
     */
    handleOptionClick(option, autoClose = true) {
        if (this.disabled || !this.open) {
            return;
        }
        // Calculate if the value changes before we actually change it –  if it's multiselect it's always changing.
        let triggerDropdownSelectionEvent = false;
        if (this.multiselect || !objectsAreEqual(this._selected, option)) {
            triggerDropdownSelectionEvent = true;
        }
        if (this.optionIsSelected(option)) {
            if (this.multiselect) {
                this.removeSelection(option);
            }
        }
        else {
            if (this.multiselect && this.maxSelections && this.maxSelections <= this._selected.length) {
                this.maxItemsSelected.emit({ maxSelections: this.maxSelections });
            }
            else {
                this.addSelection(option);
            }
        }
        this.selected = JSON.stringify(this._selected);
        // Emit event only after the value has been assigned, so it is retrievable from the HTML markup.
        if (triggerDropdownSelectionEvent) {
            this.dropdownSelection.emit(this._selected);
        }
        if (!this.multiselect && autoClose) {
            this.open = false;
        }
    }
    /**
     * @param option
     * @private
     */
    removeSelection(option) {
        if (this.multiselect) {
            const index = this._selected.findIndex(x => x.value === option.value);
            if (index >= 0) {
                this._selected.splice(index, 1);
            }
        }
        else {
            this._selected = undefined;
        }
        forceUpdate(this);
    }
    /**
     * @param option
     * @private
     */
    addSelection(option) {
        if (this.multiselect) {
            this._selected.push(option);
        }
        else {
            this._selected = option;
        }
        forceUpdate(this);
    }
    /**
     * @private
     * Toggle the visibility of the dropdown.
     */
    toggleDropdown() {
        var _a, _b;
        if (!this.disabled) {
            this.open = !this.open;
            if (this.open) {
                this.openMenu.emit();
                (_a = this.selectEl) === null || _a === void 0 ? void 0 : _a.focus();
                // Timeout is to counteract the fact that the dropdown is hidden and may not be opened/rendered at this point.
                setTimeout(() => {
                    let index = 0;
                    let selectedOption = this.multiselect && this._selected && this._selected.length > 0 ? this._selected[0] : (!this.multiselect && this._selected ? this._selected : null);
                    if (selectedOption && ((selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value) || (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label))) {
                        index = this._options.findIndex(x => x.value === (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value));
                    }
                    this.focusOnIndex(index);
                }, 15);
            }
            else {
                this.closeMenu.emit();
                (_b = this.selectEl) === null || _b === void 0 ? void 0 : _b.focus();
            }
        }
    }
    /**
     *
     * @param index
     * @private
     */
    focusOnIndex(index) {
        const el = this.hostElement.shadowRoot.querySelector(`#${this._internalId}_${index}`);
        if (el) {
            // This trick is to focus on the first element of the select once it's opened.
            el.contentEditable = 'true';
            el === null || el === void 0 ? void 0 : el.focus();
            el.contentEditable = 'false';
        }
    }
    /**
     * @private
     * @return string
     */
    getFinalCssClasses() {
        return mergeClassNames({
            [this.names.CLASS.ROOT]: true,
            [this.names.CLASS.____UP]: this.direction === 'up',
            [this.names.CLASS.____ERROR]: this.shouldRenderErrors(),
            [this.names.CLASS.____IS_OPEN]: this.open,
            [this.names.CLASS.____DISABLED]: !this.open && this.disabled,
        });
    }
    /**
     * @param option
     * @private
     */
    optionIsSelected(option) {
        if (this.multiselect) {
            return this._selected.findIndex(x => x.value === option.value) >= 0;
        }
        else {
            return option && this._selected && this._selected.value === option.value;
        }
    }
    /**
     *
     * @private
     */
    renderErrors() {
        if (this.shouldRenderErrors()) {
            return (h("ace-error", { errors: this._errors, id: 'requirements', class: this.names.CLASS.ERRORS_WRAPPER }));
        }
    }
    /**
     * @private
     */
    renderSelection() {
        const renderMultiselectSelections = () => {
            const hasOptionSelected = this._selected && this._selected.length > 0;
            let cssClasses = {
                [this.names.CLASS.____PLACEHOLDER]: !hasOptionSelected,
            };
            return (h("span", { class: cssClasses, "data-component-id": this._internalId }, hasOptionSelected ? this._selected.map(option => option.label).join(', ') : this.placeholder));
        };
        // We have to do it like this: when we JSON.stringify(this._selected) the selected option (line 137), it loses
        // its prototype and therefore the functions.
        const selectedItem = this._options.find(item => { var _a; return item.value === ((_a = this._selected) === null || _a === void 0 ? void 0 : _a.value); });
        const customTemplate = this.getSingleOptionMarkupFromTemplate(selectedItem);
        const iconColor = this.disabled ? 'text-neutral--disabled' : 'text-neutral--interactive';
        let listItemAttrs = {
            'id': this.names.ID.INPUT,
            'class': mergeClassNames({ [this.names.CLASS.INPUT]: true, [this.names.CLASS.INPUT____WITH_TEMPLATE]: !!customTemplate }),
            'onClick': () => this.toggleDropdown(),
            'aria-labelledby': this._internalId,
            'role': 'button',
            'data-component-id': this._internalId,
            'aria-multiselectable': this.multiselect.toString(),
        };
        return (h("li", Object.assign({}, listItemAttrs, { ref: el => (this.selectedFieldRef = el) }), this.multiselect ? renderMultiselectSelections() : (customTemplate ? customTemplate : this.renderOptionItem(this._selected, true, this.disabled)), h("ace-icon", { "data-component-id": this._internalId, name: 'chevron-down', class: this.names.CLASS.DROPDOWN_TOGGLE, size: 's', color: iconColor })));
    }
    /**
     *
     * @param option
     * @param forSelectedPlacement
     * @param isDisabled
     * @private
     */
    renderOptionItem(option, forSelectedPlacement = false, isDisabled = false) {
        const paymentMethod = getProperty(option, 'options.payment_method');
        const iconName = getProperty(option, 'options.icon_name');
        const imageSrc = getProperty(option, 'options.image');
        const color = isDisabled
            ? 'text-neutral--disabled'
            : !forSelectedPlacement && this.optionIsSelected(option)
                ? 'text-neutral--interactive-uni-light'
                : 'text-neutral--interactive';
        const cssClasses = {
            [this.names.CLASS.____PLACEHOLDER]: forSelectedPlacement && ((!this.multiselect && Object.keys(this._selected).length <= 0) || (this.multiselect && this._selected.length <= 0)),
        };
        return (h(Fragment, null, paymentMethod && h("ace-payment-method-icon", { "data-component-id": this._internalId, "payment-method": paymentMethod }), iconName && h("ace-icon", { "data-component-id": this._internalId, name: iconName, size: 's', color: color }), imageSrc && h("ace-image", { "data-component-id": this._internalId, src: imageSrc, width: '24' }), h("span", { class: cssClasses, "data-component-id": this._internalId }, getProperty(option, 'label', forSelectedPlacement ? this.placeholder : null))));
    }
    /**
     *
     * @param option
     * @private
     */
    getSingleOptionMarkupFromTemplate(option) {
        const templateFnc = getProperty(option, 'template');
        if (templateFnc) {
            let renderedTemplate = templateFnc instanceof Function ? templateFnc(option, this.optionIsSelected(option)) : eval(templateFnc)(option, this.optionIsSelected(option));
            return parseStringToHtmlNodes(renderedTemplate);
        }
        return null;
    }
    performActionForKeyOnItem(e, option = null, index = null) {
        const keyCode = e.code;
        const stopEvents = () => {
            e.preventDefault();
            e.stopPropagation();
        };
        const jumpToItem = (direction, index) => {
            stopEvents();
            if (option) {
                this.focusOnNextOption(direction, index);
            }
            else {
                this.toggleDropdown();
            }
        };
        // keyboard interactions have been implemented from standards (https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/listbox_role)
        // and using the default select behaviour as per W3C (https://www.w3schools.com/tags/tryit.asp?filename=tryhtml_select)
        switch (keyCode) {
            case ENTER_KEY_CODE:
            case SPACEBAR_KEY_CODE:
                // On space: select/unselect current item. Close dropdown ONLY if it's single select – for multiselect, leave it open.
                // On enter: select/unselect current item AND close dropdown.
                stopEvents();
                if (option) {
                    this.handleOptionClick(option, false);
                    if (!this.multiselect || keyCode === ENTER_KEY_CODE) {
                        this.toggleDropdown();
                    }
                }
                else {
                    this.toggleDropdown();
                }
                return;
            case TAB_KEY_CODE:
                // On tab: close the dropdown.
                this.closeDropdown(e, true);
                return;
            case DOWN_ARROW_KEY_CODE:
                // On arrow down: move down to the list of items.
                jumpToItem(DOWN_ARROW_KEY_CODE, index);
                return;
            case UP_ARROW_KEY_CODE:
                // On arrow up: move up to the list of items.
                jumpToItem(UP_ARROW_KEY_CODE, index);
                return;
            case ESCAPE_KEY_CODE:
                // On escape: close the dropdown but leave element focused.
                e.preventDefault();
                e.stopPropagation();
                this.toggleDropdown();
                return;
            default:
                return;
        }
    }
    /**
     *
     * @param direction
     * @param currentIndex
     * @private
     */
    focusOnNextOption(direction, currentIndex) {
        const maxIndex = this._options.length - 1;
        const noMoreDown = currentIndex >= maxIndex && direction === DOWN_ARROW_KEY_CODE;
        const noMoreUp = currentIndex === 0 && direction === UP_ARROW_KEY_CODE;
        let newIndex;
        if (noMoreDown || noMoreUp) {
            newIndex = currentIndex;
        }
        else {
            newIndex = currentIndex + (direction === UP_ARROW_KEY_CODE ? -1 : 1);
        }
        this.focusOnIndex(newIndex);
    }
    /**
     * @private
     */
    renderOption(option, index) {
        const isSelected = this.optionIsSelected(option);
        const customTemplate = this.getSingleOptionMarkupFromTemplate(option);
        const optionClasses = mergeClassNames({
            [this.names.CLASS.OPTION]: true,
            [this.names.CLASS.OPTION____SELECTED]: isSelected,
            [this.names.CLASS.OPTION____WITH_TEMPLATE]: !!customTemplate
        });
        let itemAttrs = {
            'class': optionClasses,
            'onClick': () => this.handleOptionClick(option),
            'onKeyDown': e => this.performActionForKeyOnItem(e, option, index),
            'aria-expanded': this.open.toString(),
            'tabindex': '-1',
            'aria-selected': isSelected.toString(),
            'id': `${this._internalId}_${index}`,
            'data-component-id': this._internalId,
            'role': 'option',
        };
        return h("li", Object.assign({}, itemAttrs), customTemplate ? customTemplate : this.renderOptionItem(option));
    }
    getCustomStyles() {
        var _a;
        return createCssVariables(Object.assign({ [aceSelectCssVariables.MIN_WIDTH]: this.minWidth }, (((_a = this.selectedFieldRef) === null || _a === void 0 ? void 0 : _a.offsetHeight) && {
            [aceSelectCssVariables.SELECTION_HEIGHT]: `calc(${this.selectedFieldRef.offsetHeight}px + ${(this.open ? '2 * var(--ace-semantic-form-fields-select-border-width)' : '0px')})`,
        })));
    }
    render() {
        return (h("div", { key: '3ac20b6dec8a3da17bae1e32f454c5d171a02ae1', id: this.names.ID.ROOT, class: this.getFinalCssClasses(), style: this.getCustomStyles(), tabindex: '0', onKeyDown: e => this.performActionForKeyOnItem(e), ref: el => (this.selectEl = el) }, this.label ? (h("label", { "data-component-id": this._internalId, class: this.names.CLASS.LABEL, htmlFor: this._internalId }, this.label)) : null, h("div", { key: '0edeede3904ce1fa8dac346f02bd10def1b907fc', class: this.names.CLASS.WRAPPER, "data-component-id": this._internalId, style: this.getCustomStyles() }, h("div", { key: '09d78bde9f92987120f0307eef6ace3df80a29e8', class: this.names.CLASS.CONTAINER, "data-component-id": this._internalId }, this.renderSelection(), h("ul", { key: '712eafcddf358860516ecdfc2a1967f43c8a7c5e', class: this.names.CLASS.OPTIONS, role: 'listbox', style: { 'max-height': this.maxHeight }, "data-component-id": this._internalId }, this._options.map((option, index) => this.renderOption(option, index))))), this.renderErrors()));
    }
    static get is() { return "ace-select"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["./ace-select.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ace-select.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "label",
                "reflect": false
            },
            "direction": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceSelectDirection",
                    "resolved": "\"down\" | \"up\"",
                    "references": {
                        "IAceSelectDirection": {
                            "location": "import",
                            "path": "./ace-select.types",
                            "id": "src/components/ace-select/ace-select.types.ts::IAceSelectDirection"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "direction",
                "reflect": false,
                "defaultValue": "'down'"
            },
            "options": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceSelectOption[] | string",
                    "resolved": "IAceSelectOption[] | string",
                    "references": {
                        "IAceSelectOption": {
                            "location": "import",
                            "path": "./ace-select.types",
                            "id": "src/components/ace-select/ace-select.types.ts::IAceSelectOption"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "options",
                "reflect": false,
                "defaultValue": "[]"
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "placeholder",
                "reflect": false
            },
            "customCss": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "custom-css",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "multiselect": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "multiselect",
                "reflect": true,
                "defaultValue": "false"
            },
            "selected": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "IAceSelectOption | IAceSelectOption[] | string",
                    "resolved": "IAceSelectOption[] | string | { label: string; value: string; template?: Function; options?: { payment_method?: IAcePaymentMethod; icon_name?: string; image?: string; }; }",
                    "references": {
                        "IAceSelectOption": {
                            "location": "import",
                            "path": "./ace-select.types",
                            "id": "src/components/ace-select/ace-select.types.ts::IAceSelectOption"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "selected",
                "reflect": true
            },
            "maxSelections": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "max-selections",
                "reflect": false
            },
            "maxHeight": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "max-height",
                "reflect": false
            },
            "minWidth": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "min-width",
                "reflect": false
            },
            "errors": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IError[] | string",
                    "resolved": "IError[] | string",
                    "references": {
                        "IError": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/types.ts::IError"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "errors",
                "reflect": false,
                "defaultValue": "[]"
            },
            "hasErrors": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "has-errors",
                "reflect": true,
                "defaultValue": "false"
            },
            "open": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "open",
                "reflect": true,
                "defaultValue": "false"
            }
        };
    }
    static get events() {
        return [{
                "method": "dropdownSelection",
                "name": "dropdownSelection",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "Object",
                    "resolved": "Object",
                    "references": {
                        "Object": {
                            "location": "global",
                            "id": "global::Object"
                        }
                    }
                }
            }, {
                "method": "maxItemsSelected",
                "name": "maxItemsSelected",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "Object",
                    "resolved": "Object",
                    "references": {
                        "Object": {
                            "location": "global",
                            "id": "global::Object"
                        }
                    }
                }
            }, {
                "method": "openMenu",
                "name": "openMenu",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "Object",
                    "resolved": "Object",
                    "references": {
                        "Object": {
                            "location": "global",
                            "id": "global::Object"
                        }
                    }
                }
            }, {
                "method": "closeMenu",
                "name": "closeMenu",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "Object",
                    "resolved": "Object",
                    "references": {
                        "Object": {
                            "location": "global",
                            "id": "global::Object"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "hostElement"; }
    static get watchers() {
        return [{
                "propName": "options",
                "methodName": "parseOptions"
            }, {
                "propName": "selected",
                "methodName": "parseSelected"
            }, {
                "propName": "errors",
                "methodName": "parseErrors"
            }, {
                "propName": "customCss",
                "methodName": "parseAndApplyCustomCss"
            }];
    }
    static get listeners() {
        return [{
                "name": "click",
                "method": "closeDropdown",
                "target": "document",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=ace-select.js.map
